import { useParams, Link } from "react-router-dom";
import { Navbar, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { services, reviews } from "@/lib/mockData";
import { Star, MapPin, CheckCircle, Clock, Calendar, MessageSquare } from "lucide-react";
import { BackButton } from "@/components/BackButton";
import { useState } from "react";
import { useSeo } from "@/hooks/useSeo";
import { toast } from "sonner";

const serviceTypeLabels: Record<string, string> = {
  tradfallning: "Trädfällning",
  gallring: "Gallring",
  bortforsling: "Bortforsling",
  "kapning-ved": "Kapning/Ved",
  transport: "Transport",
  sagning: "Sågning",
};

export default function ServiceDetail() {
  const { id } = useParams();
  const service = services.find((s) => s.id === id);
  const [selectedDate, setSelectedDate] = useState("");
  const [notes, setNotes] = useState("");

  useSeo({
    title: service ? `${service.title} — ${service.provider.name}` : "Skogstjänst",
    description: service
      ? `${service.title} via ${service.provider.name} i ${service.provider.location}. Boka skogstjänst tryggt på TimberNet.`.slice(0, 160)
      : "Boka skogstjänster från verifierade leverantörer på TimberNet.",
    path: `/skogstjanster/${id ?? ""}`,
  });

  if (!service) {
    return (
      <div className="flex min-h-screen flex-col">
        <Navbar />
        <div className="container flex flex-1 items-center justify-center py-20 text-center">
          <div>
            <p className="text-lg font-medium">Tjänsten hittades inte</p>
            <BackButton fallback="/skogstjanster" />
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const priceDisplay = () => {
    if (service.priceModel === "timme") return `${service.basePrice} kr/tim`;
    if (service.priceModel === "från") return `Från ${service.basePrice.toLocaleString("sv-SE")} kr`;
    return `${service.basePrice.toLocaleString("sv-SE")} kr`;
  };

  const handleBook = () => {
    if (!selectedDate) {
      toast.error("Välj ett datum för bokningen");
      return;
    }
    toast.success("Bokningsförfrågan skickad! Du får svar inom 24 timmar.");
  };

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      <div className="container flex-1 py-8">
        <BackButton fallback="/skogstjanster" label="Tillbaka till tjänster" className="mb-6 text-muted-foreground" />

        <div className="grid gap-8 lg:grid-cols-3">
          {/* Main content */}
          <div className="lg:col-span-2">
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Badge variant="secondary">{serviceTypeLabels[service.serviceType]}</Badge>
              {service.provider.verified && (
                <Badge variant="outline" className="gap-1">
                  <CheckCircle className="h-3 w-3" /> Verifierad
                </Badge>
              )}
            </div>

            <h1 className="mb-2 font-display text-2xl font-bold md:text-3xl">{service.title}</h1>
            <p className="mb-6 text-muted-foreground">{service.provider.name} • {service.provider.type === "företag" ? "Företag" : "Hobbyaktör"}</p>

            <div className="mb-6 flex flex-wrap gap-4 text-sm">
              <span className="flex items-center gap-1"><Star className="h-4 w-4 fill-accent text-accent" /> {service.provider.rating} ({service.provider.reviewCount} omdömen)</span>
              <span className="flex items-center gap-1"><MapPin className="h-4 w-4 text-muted-foreground" /> {service.provider.location}</span>
              <span className="flex items-center gap-1"><Clock className="h-4 w-4 text-muted-foreground" /> {service.availability}</span>
            </div>

            <div className="mb-8 rounded-lg border border-border bg-card p-6">
              <h2 className="mb-3 font-sans text-lg font-semibold">Beskrivning</h2>
              <p className="text-sm leading-relaxed text-muted-foreground">{service.description}</p>
              {service.travelFee && (
                <p className="mt-3 text-sm text-muted-foreground"><strong>Reseersättning:</strong> {service.travelFee}</p>
              )}
              {service.certifications.length > 0 && (
                <div className="mt-4">
                  <h3 className="mb-2 font-sans text-sm font-semibold">Certifieringar</h3>
                  <div className="flex flex-wrap gap-2">
                    {service.certifications.map((c) => (
                      <Badge key={c} variant="secondary">{c}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Reviews */}
            <h2 className="mb-4 font-sans text-lg font-semibold">Omdömen</h2>
            <div className="space-y-4">
              {reviews.map((r) => (
                <div key={r.id} className="rounded-lg border border-border bg-card p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{r.author}</span>
                      <div className="flex">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star key={i} className={`h-3.5 w-3.5 ${i < r.rating ? "fill-accent text-accent" : "text-border"}`} />
                        ))}
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground">{r.date}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{r.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Booking widget */}
          <div className="lg:col-span-1">
            <div className="sticky top-20 rounded-lg border border-border bg-card p-6 shadow-card">
              <p className="mb-1 text-2xl font-bold text-primary">{priceDisplay()}</p>
              <p className="mb-6 text-xs text-muted-foreground">Priserna är exklusive moms</p>

              <div className="space-y-4">
                <div>
                  <label className="mb-1.5 block text-sm font-medium">Datum</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="pl-9" />
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium">Meddelande (valfritt)</label>
                  <Textarea placeholder="Beskriv ditt uppdrag…" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
                </div>
                <Button className="w-full" size="lg" onClick={handleBook}>
                  Skicka bokningsförfrågan
                </Button>
                <Button variant="outline" className="w-full" size="sm">
                  <MessageSquare className="mr-2 h-4 w-4" /> Skicka meddelande
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
